import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Pagination from '../../component/Admin/Pagination';
import {
    FaEdit,
    FaPlusCircle,
    FaTrashAlt,
    FaSearch,
    FaArrowLeft,
    FaBook,
    FaCalendarAlt,
    FaLayerGroup,
} from 'react-icons/fa';
import Modal from 'react-modal';
import Swal from 'sweetalert2';
import { encryptId } from '../../helper/helper';
import { formatIssuePeriod } from '../../helper/issuePeriod';
import { canAccess } from '../../helper/permissions';
import { apiHost } from '../../helper/apiHost';
import { headerImageUrl, withLegacyImageFallback } from '../../helper/mediaUrl';

const emptyForm = {
    volume: '',
    issue: '',
    start_month: '',
    start_year: '',
    end_month: '',
    end_year: '',
    maxPublish: '',
    description: '',
};

const months = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember',
];

const fieldClass =
    'rounded-lg block w-full px-3 py-2 text-base sm:text-sm text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors';

const labelClass =
    'block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5 text-left';

const Issues = ({ data }) => {
    const navigate = useNavigate();
    const { journalId } = useParams();
    const host = apiHost;

    const header = {
        headers: { Authorization: `Bearer ${data.token}` },
    };
    const permissions = data?.user?.permissions || {};
    const canCreate = canAccess(permissions, 'journal', 'create');
    const canEdit = canAccess(permissions, 'journal', 'edit');
    const canDelete = canAccess(permissions, 'journal', 'delete');

    const [listIssues, setListIssues] = useState([]);
    const [journal, setJournal] = useState(null);
    const [meta, setMeta] = useState({});
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(12);
    const [search, setSearch] = useState('');
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [formData, setFormData] = useState({ ...emptyForm });

    useEffect(() => {
        if (journalId) {
            getJournal();
            getListIssues();
        }
        // eslint-disable-next-line
    }, [page, limit, search, journalId]);

    const getJournal = () => {
        axios
            .get(`api/journal/${encryptId(journalId)}`, header)
            .then((response) => setJournal(response.data))
            .catch((error) => console.error('Error fetching journal:', error));
    };

    const getListIssues = () => {
        const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
        axios
            .get(`api/issues?page=${page}&limit=${limit}&journal=${journalId}${searchParam}`, header)
            .then((response) => {
                setMeta(response.data.meta);
                setListIssues(response.data.items);
            })
            .catch((error) => console.error('Error fetching issues:', error));
    };

    const selectPage = (value) => setPage(value);

    const nextPage = () => {
        if (page < meta.totalPages) setPage(page + 1);
    };

    const prevPage = () => {
        if (page > 1) setPage(page - 1);
    };

    const limitPage = (event) => {
        setLimit(Number(event.target.value));
        setPage(1);
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const payload = {
            ...formData,
            journal_id: parseInt(journalId, 10),
            maxPublish: parseInt(formData.maxPublish, 10),
        };

        const request = formData.issuesID
            ? axios.patch(`api/issues/${formData.issuesID}`, payload, header)
            : axios.post('api/issues', payload, header);

        request
            .then(() => {
                getListIssues();
                closeModal();
                Swal.fire({
                    title: 'Success',
                    icon: 'success',
                    text: formData.issuesID ? 'Issue berhasil diupdate' : 'Issue berhasil ditambahkan',
                    confirmButtonText: 'Tutup',
                    confirmButtonColor: '#4569ab',
                });
            })
            .catch((error) => {
                console.error('Error saving issue:', error);
                Swal.fire({
                    title: 'Error',
                    text: error.response?.data?.message || 'Terjadi kesalahan saat menyimpan issue',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            });
    };

    const handleClickEdit = (id) => {
        const issue = listIssues.find((item) => item.issuesID === id);
        if (!issue) return;
        setFormData({
            issuesID: issue.issuesID,
            volume: issue.volume || '',
            issue: issue.issue || '',
            start_month: issue.start_month || issue.month || '',
            start_year: issue.start_year || issue.year || '',
            end_month: issue.end_month || issue.month || '',
            end_year: issue.end_year || issue.year || '',
            maxPublish: issue.maxPublish || '',
            description: issue.description || '',
        });
        setModalIsOpen(true);
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Hapus Issue?',
            text: 'Tindakan ini tidak dapat dibatalkan.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#5C5E62',
            confirmButtonText: 'Ya, hapus',
            cancelButtonText: 'Batal',
        }).then((result) => {
            if (!result.isConfirmed) return;
            axios
                .delete(`api/issues/${id}`, header)
                .then(() => {
                    getListIssues();
                    Swal.fire({
                        title: 'Success',
                        icon: 'success',
                        text: 'Issue berhasil dihapus',
                        confirmButtonText: 'Tutup',
                        confirmButtonColor: '#4569ab',
                    });
                })
                .catch((error) => {
                    console.error('Error deleting issue:', error);
                    Swal.fire({
                        title: 'Gagal',
                        text:
                            error.response?.data?.message ||
                            error.response?.data ||
                            'Terjadi kesalahan saat menghapus issue.',
                        icon: 'error',
                        confirmButtonText: 'OK',
                    });
                });
        });
    };

    const openAddModal = () => {
        setFormData({ ...emptyForm });
        setModalIsOpen(true);
    };

    const closeModal = () => {
        setFormData({ ...emptyForm });
        setModalIsOpen(false);
    };

    const handleChangeInput = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const warnLocked = (item, action) => {
        Swal.fire({
            title: `Tidak Dapat ${action}`,
            text: `Issue ini sudah digunakan oleh ${item.publishedCount || 0} LOA.`,
            icon: 'warning',
            confirmButtonText: 'OK',
            confirmButtonColor: '#4569ab',
        });
    };

    const totalIssues = meta.totalItems ?? listIssues.length;

    return (
        <>
            <div className="min-h-screen p-4 md:p-6 bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
                <div className="mx-auto max-w-7xl space-y-5">
                    {/* Journal context strip */}
                    {journal && (
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-xl border border-primary/15 bg-white dark:bg-gray-800 dark:border-gray-700 px-4 py-3 shadow-sm">
                            <button
                                onClick={() => navigate('/admin/journal')}
                                className="btn-secondary !min-h-9 shrink-0"
                                type="button"
                            >
                                <FaArrowLeft />
                                Kembali
                            </button>
                            <div className="flex h-12 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-50 dark:bg-gray-700">
                                {journal.header ? (
                                    <img
                                        src={headerImageUrl(journal.header)}
                                        onError={(e) => withLegacyImageFallback(e, journal.header)}
                                        className="h-full w-auto max-w-full object-contain"
                                        alt={journal.journal_name}
                                    />
                                ) : (
                                    <FaBook className="text-primary/40 text-xl" />
                                )}
                            </div>
                            <div className="min-w-0 flex-1 text-left">
                                <p className="text-[11px] font-semibold uppercase tracking-wider text-primary dark:text-primary-on-dark">
                                    Journal
                                </p>
                                <h2 className="truncate text-base md:text-lg font-bold text-gray-900 dark:text-white">
                                    {journal.journal_name}
                                </h2>
                                {journal.journal_manager && (
                                    <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                                        {journal.journal_manager}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Toolbar — tepat di atas daftar issue */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        {!journal ? (
                            <button
                                onClick={() => navigate('/admin/journal')}
                                className="btn-secondary !min-h-9 shrink-0"
                                type="button"
                            >
                                <FaArrowLeft />
                                Kembali
                            </button>
                        ) : null}
                        <div className="relative flex-1 max-w-md">
                            <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 py-2.5 pl-9 pr-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                                placeholder="Cari volume, issue, periode..."
                            />
                        </div>
                        <div className="flex items-center gap-2 sm:ml-auto">
                            <div className="inline-flex items-center gap-2 rounded-lg bg-primary/10 dark:bg-primary/20 px-3 py-2 text-sm font-bold text-primary dark:text-primary-on-dark">
                                <FaLayerGroup className="opacity-80" />
                                {totalIssues} Issue
                            </div>
                            {canCreate ? (
                                <button
                                    onClick={openAddModal}
                                    className="btn-primary !shadow-none"
                                    type="button"
                                >
                                    <FaPlusCircle />
                                    Tambah Issue
                                </button>
                            ) : null}
                        </div>
                    </div>

                    {/* Issue cards */}
                    {listIssues.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {listIssues.map((item) => {
                                const published = Number(item.publishedCount) || 0;
                                const max = Number(item.maxPublish) || 0;
                                const pct = max > 0 ? Math.min(100, Math.round((published / max) * 100)) : 0;
                                const isFull = item.isFull || (max > 0 && published >= max);
                                const isUsed = item.isUsed || published > 0;

                                return (
                                    <div
                                        key={item.issuesID}
                                        className="group flex flex-col rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md hover:border-primary/40 dark:hover:border-primary/50 transition-all duration-200 overflow-hidden"
                                    >
                                        <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3 border-b border-gray-100 dark:border-gray-700/80">
                                            <div className="min-w-0 text-left">
                                                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">
                                                    Volume {item.volume || '-'}
                                                </p>
                                                <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                                                    Issue {item.issue || '-'}
                                                </h3>
                                            </div>
                                            <span
                                                className={`shrink-0 rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-wide ${
                                                    isFull
                                                        ? 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300'
                                                        : isUsed
                                                          ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                                                          : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                                                }`}
                                            >
                                                {isFull ? 'Penuh' : isUsed ? 'Terpakai' : 'Kosong'}
                                            </span>
                                        </div>

                                        <div className="flex-1 px-4 py-3 space-y-3">
                                            <div className="flex items-start gap-2 text-left">
                                                <FaCalendarAlt className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary dark:text-primary-on-dark" />
                                                <div>
                                                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                                        Periode
                                                    </p>
                                                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                                        {formatIssuePeriod(item) || '-'}
                                                    </p>
                                                </div>
                                            </div>

                                            <div>
                                                <div className="mb-1.5 flex items-center justify-between text-xs">
                                                    <span className="font-semibold text-gray-500 dark:text-gray-400">
                                                        Kapasitas LOA
                                                    </span>
                                                    <span className="font-bold text-gray-800 dark:text-gray-200">
                                                        {published}/{max || '—'}
                                                    </span>
                                                </div>
                                                <div className="h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                                                    <div
                                                        className={`h-full rounded-full transition-all ${
                                                            isFull
                                                                ? 'bg-red-500'
                                                                : pct > 70
                                                                  ? 'bg-amber-500'
                                                                  : 'bg-primary'
                                                        }`}
                                                        style={{ width: `${pct}%` }}
                                                    />
                                                </div>
                                            </div>

                                            {item.description ? (
                                                <p className="line-clamp-2 text-left text-xs text-gray-500 dark:text-gray-400">
                                                    {item.description}
                                                </p>
                                            ) : null}
                                        </div>

                                        {(canEdit || canDelete) ? (
                                            <div className="flex gap-2 border-t border-gray-100 dark:border-gray-700 px-4 py-3">
                                                {canEdit ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleClickEdit(item.issuesID)}
                                                        className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold transition-colors bg-primary/10 text-primary hover:bg-primary hover:text-white dark:bg-primary/20 dark:text-primary-on-dark"
                                                        title="Edit"
                                                    >
                                                        <FaEdit />
                                                        Edit
                                                    </button>
                                                ) : null}
                                                {canDelete ? (
                                                    <button
                                                        type="button"
                                                        disabled={isUsed}
                                                        onClick={() =>
                                                            isUsed
                                                                ? warnLocked(item, 'Dihapus')
                                                                : handleDelete(item.issuesID)
                                                        }
                                                        className={`inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${
                                                            isUsed
                                                                ? 'cursor-not-allowed bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
                                                                : 'bg-red-50 text-red-600 hover:bg-red-600 hover:text-white dark:bg-red-900/30 dark:text-red-400'
                                                        }`}
                                                        title={isUsed ? 'Sudah dipakai LOA' : 'Hapus'}
                                                        aria-label="Hapus"
                                                    >
                                                        <FaTrashAlt className="h-3.5 w-3.5" />
                                                    </button>
                                                ) : null}
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-6 py-14 text-center shadow-sm">
                            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20">
                                <FaLayerGroup className="h-6 w-6 text-primary dark:text-primary-on-dark" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">
                                {search ? 'Issue tidak ditemukan' : 'Belum ada Issue'}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 max-w-sm mx-auto">
                                {search
                                    ? `Tidak ada hasil untuk “${search}”.`
                                    : 'Tambahkan issue pertama untuk volume dan periode publikasi journal ini.'}
                            </p>
                            {!search && canCreate && (
                                <button
                                    type="button"
                                    onClick={openAddModal}
                                    className="btn-primary"
                                >
                                    <FaPlusCircle />
                                    Tambah Issue
                                </button>
                            )}
                        </div>
                    )}

                    {listIssues.length > 0 && (
                        <div className="pt-1">
                            <Pagination
                                currentPage={page}
                                totalPages={meta.totalPages}
                                limit={limit}
                                limitPage={limitPage}
                                selectPage={selectPage}
                                nextPage={nextPage}
                                prevPage={prevPage}
                            />
                        </div>
                    )}
                </div>
            </div>

            <Modal
                isOpen={modalIsOpen}
                onRequestClose={closeModal}
                contentLabel="Issue Modal"
                ariaHideApp={false}
                className="fixed inset-0 z-50 flex items-center justify-center p-4 outline-none"
                overlayClassName="fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px]"
                shouldCloseOnOverlayClick={true}
            >
                <form
                    onSubmit={handleSubmit}
                    className="relative flex w-full max-w-lg max-h-[90vh] flex-col overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-2xl"
                >
                    <div className="relative flex shrink-0 items-center justify-between border-b border-gray-100 dark:border-gray-700 px-5 py-4">
                        <div className="text-left">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-primary dark:text-primary-on-dark">
                                Issue
                            </p>
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                                {formData.issuesID ? 'Edit Issue' : 'Tambah Issue'}
                            </h2>
                        </div>
                        <button
                            type="button"
                            onClick={closeModal}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 text-xl leading-none"
                            aria-label="Tutup"
                        >
                            ×
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label htmlFor="volume" className={labelClass}>
                                    Volume <sup className="text-red-500">*</sup>
                                </label>
                                <input
                                    id="volume"
                                    type="text"
                                    name="volume"
                                    placeholder="cth. 12"
                                    className={fieldClass}
                                    required
                                    value={formData.volume}
                                    onChange={handleChangeInput}
                                />
                            </div>
                            <div>
                                <label htmlFor="issue" className={labelClass}>
                                    Nomor Issue <sup className="text-red-500">*</sup>
                                </label>
                                <input
                                    id="issue"
                                    type="text"
                                    name="issue"
                                    placeholder="cth. 1"
                                    className={fieldClass}
                                    required
                                    value={formData.issue}
                                    onChange={handleChangeInput}
                                />
                            </div>
                        </div>

                        <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-3 space-y-3">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 text-left">
                                Periode
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label htmlFor="start_month" className={labelClass}>
                                        Bulan Awal <sup className="text-red-500">*</sup>
                                    </label>
                                    <select
                                        id="start_month"
                                        name="start_month"
                                        className={fieldClass}
                                        required
                                        value={formData.start_month}
                                        onChange={handleChangeInput}
                                    >
                                        <option value="">Pilih</option>
                                        {months.map((month) => (
                                            <option key={month} value={month}>
                                                {month}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="start_year" className={labelClass}>
                                        Tahun Awal <sup className="text-red-500">*</sup>
                                    </label>
                                    <input
                                        id="start_year"
                                        type="text"
                                        name="start_year"
                                        placeholder="2026"
                                        className={fieldClass}
                                        required
                                        value={formData.start_year}
                                        onChange={handleChangeInput}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="end_month" className={labelClass}>
                                        Bulan Akhir <sup className="text-red-500">*</sup>
                                    </label>
                                    <select
                                        id="end_month"
                                        name="end_month"
                                        className={fieldClass}
                                        required
                                        value={formData.end_month}
                                        onChange={handleChangeInput}
                                    >
                                        <option value="">Pilih</option>
                                        {months.map((month) => (
                                            <option key={month} value={month}>
                                                {month}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="end_year" className={labelClass}>
                                        Tahun Akhir <sup className="text-red-500">*</sup>
                                    </label>
                                    <input
                                        id="end_year"
                                        type="text"
                                        name="end_year"
                                        placeholder="2026"
                                        className={fieldClass}
                                        required
                                        value={formData.end_year}
                                        onChange={handleChangeInput}
                                    />
                                </div>
                            </div>
                        </div>

                        <div>
                            <label htmlFor="maxPublish" className={labelClass}>
                                Max Publish <sup className="text-red-500">*</sup>
                            </label>
                            <input
                                id="maxPublish"
                                type="number"
                                name="maxPublish"
                                placeholder="Maksimal LOA per issue"
                                className={fieldClass}
                                required
                                min="1"
                                value={formData.maxPublish}
                                onChange={handleChangeInput}
                            />
                        </div>

                        <div>
                            <label htmlFor="description" className={labelClass}>
                                Keterangan
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                placeholder="Opsional"
                                rows="2"
                                className={`${fieldClass} resize-none`}
                                value={formData.description}
                                onChange={handleChangeInput}
                            />
                        </div>
                    </div>

                    <div className="flex shrink-0 gap-2 border-t border-gray-100 dark:border-gray-700 px-5 py-4">
                        <button
                            type="button"
                            onClick={closeModal}
                            className="flex-1 rounded-lg bg-gray-100 dark:bg-gray-700 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                            Batal
                        </button>
                        <button
                            type="submit"
                            className="flex-1 btn-primary"
                        >
                            Simpan
                        </button>
                    </div>
                </form>
            </Modal>
        </>
    );
};

export default Issues;
